import { useCreateForm } from "@/features/qualifications/hooks/use-qualification-forms";
import type {
  CreateFormRequest,
  QualificationFormTemplate,
  UpdateFormRequest,
} from "@/features/qualifications/types";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AFTER_SAVE } from "@/components/molecules/form-footer";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { PageLayout } from "../layout";
import { FormEditor } from "./components/form-editor";
import { TemplateSelector } from "./components/template-selector";

export function CreateQualificationFormPage() {
  const { t } = useTranslation("qualifications");
  const navigate = useNavigate();
  const { company: clientId } = useParams<{ company: string }>();
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] =
    useState<QualificationFormTemplate | null>(null);
  const [hasSelectedOption, setHasSelectedOption] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [generatedForm, setGeneratedForm] = useState<any>(null);

  const createForm = useCreateForm();

  // Check if we're showing the editor (template selected or scratch chosen)
  const showEditor =
    hasSelectedOption || location.state?.fromTemplate === false;

  const handleTemplateSelect = (template: QualificationFormTemplate | null) => {
    setSelectedTemplate(template);
    setHasSelectedOption(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleGenerateSuccess = (generated: any) => {
    setGeneratedForm(generated);
    setHasSelectedOption(true);
  };

  const listPath = `/${clientId}/settings/qualification-forms`;
  const backTo = { label: t("forms.backToForms"), path: listPath };

  const handleSubmit = async (data: CreateFormRequest | UpdateFormRequest) => {
    try {
      await createForm.mutateAsync(data as CreateFormRequest);
      toast.success(t("forms.messages.created"));
      navigate(listPath, AFTER_SAVE);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("forms.messages.createError"),
      );
    }
  };

  const handleBack = () => {
    setSelectedTemplate(null);
    setHasSelectedOption(false);
    setGeneratedForm(null);
  };

  if (showEditor) {
    const initialData = generatedForm
      ? {
          name: generatedForm.name,
          description: generatedForm.description,
          questions: { questions: generatedForm.questions },
          scoring_strategy: generatedForm.scoring_strategy,
          auto_accept_threshold: generatedForm.auto_accept_threshold,
          auto_reject_threshold: generatedForm.auto_reject_threshold,
          status: "active" as const,
        }
      : selectedTemplate
        ? {
            name: selectedTemplate.name,
            description: selectedTemplate.description,
            questions: { questions: selectedTemplate.questions },
            scoring_strategy: selectedTemplate.scoring_strategy,
            auto_accept_threshold: selectedTemplate.auto_accept_threshold,
            auto_reject_threshold: selectedTemplate.auto_reject_threshold,
            status: "active" as const,
          }
        : undefined;

    return (
      <PageLayout
        title={t("forms.createPage.title")}
        description={t("forms.createPage.description")}
        backTo={backTo}
        width="narrow"
        actions={
          // Back to the choice of a starting point — a step of this page, not
          // a page of its own, so not `backTo`.
          hasSelectedOption && (
            <Button variant="outline" onClick={handleBack}>
              <ArrowCounterClockwiseIcon className="size-4" />
              {t("forms.createPage.backToTemplates")}
            </Button>
          )
        }
      >
        <FormEditor
          isNew
          initialData={initialData}
          onSubmit={handleSubmit}
          saving={createForm.isPending}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t("forms.createPage.chooseTitle")}
      description={t("forms.createPage.chooseDescription")}
      backTo={backTo}
      width="narrow"
    >
      <TemplateSelector
        onSelectTemplate={handleTemplateSelect}
        onGenerateSuccess={handleGenerateSuccess}
      />
    </PageLayout>
  );
}
